import React from "react";
import {
  TextField,
  Typography,
  Box,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CheckCircle } from "@mui/icons-material";

interface AdornmentProps {
  text: string;
  onClick: () => void;
}

interface Option {
  value: string;
  label: string;
}

interface DynamicInputProps {
  formik?: FormikProps<any> | FormikProps<any>; // Now optional
  name: keyof any | string;
  label?: string;
  type:
    | "text"
    | "number"
    | "select"
    | "email"
    | "password"
    | "textarea"
    | "date";
  placeholder?: string;
  helperText?: string | React.ReactNode;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  adornment?: AdornmentProps | React.ReactNode;
  options?: Option[];
  verified?: boolean;
  value?: string | number;
  disabled?: boolean;
  required?: boolean;
  limit?: number;
  rows?: number;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  variant?: "outlined" | "filled" | "standard";
}

const DynamicInput: React.FC<DynamicInputProps> = ({
  formik,
  name,
  label,
  type,
  placeholder,
  helperText,
  inputProps,
  adornment,
  options,
  verified,
  value,
  disabled = false,
  required,
  limit,
  rows = 3,
  onChange,
  variant = "outlined",
}) => {
  const isFormik = !!formik;

  // Get value and handle change based on whether Formik is used
  const fieldValue = isFormik ? formik.getFieldProps(name as any).value : value ?? "";
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let newValue = e.target.value;
    if (limit && newValue.length > limit) {
      newValue = newValue.substring(0, limit);
    }
    if (type === "number") {
      newValue = newValue.replace(/[^0-9]/g, "");
    }

    if (onChange) {
      onChange(e);
    } else if (isFormik) {
      formik.setFieldValue(name as any, newValue);
    }
  };

  const hasError = isFormik
    ? Boolean(
        formik.touched[name as any ] &&
          formik.errors[name as any]
      )
    : false;

  const errorMessage = hasError
    ? String(
        formik?.errors[name as any]|| ""
      )
    : helperText || "";

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{ width: { xs: "100%", lg: "50%" } }}
      mt={1}
    >
      {/* Label */}
      {label && (
        <Typography
          variant="h6"
          mb={1}
          sx={{
            color: "#414651",
            fontFamily: "Inter",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "20px",
            textAlign: "left",
          }}
        >
          {label} {required && "*"}
        </Typography>
      )}

      {type === "date" ? (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label={placeholder}
            value={fieldValue ? dayjs(fieldValue) : null}
            format="DD/MM/YYYY HH:mm"
            minDateTime={dayjs()} // Prevents past dates/times
            disabled={disabled}
            onChange={(date) => {
              const formattedDate = date
                ? dayjs(date).format("YYYY-MM-DD HH:mm:ss")
                : "";
              if (isFormik) {
                formik.setFieldValue(name as any, formattedDate);
              } else {
                onChange?.({
                  target: { name, value: date },
                } as any);
              }
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                error: hasError,
                helperText: errorMessage,
              },
            }}
          />
        </LocalizationProvider>
      ) : type === "select" && options ? (
        /* Select Input */
        <TextField
          select
          name={name as any}
          value={fieldValue}
          disabled={disabled}
          onChange={handleChange}
          error={hasError}
          helperText={errorMessage}
          sx={{ width: "100%" }}
          slotProps={{
            select: {
              displayEmpty: true,
              renderValue(value) {
                if (!value) {
                  return <span style={{ color: "#aaa" }}>{placeholder}</span>;
                }
                return options.find((option) => option.value === value)?.label;
              },
            },
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <>
          <TextField
            fullWidth
            name={name as any}
            type={type === "textarea" ? "text" : type}
            placeholder={placeholder}
            value={fieldValue}
            disabled={disabled}
            multiline={type === "textarea"}
            rows={type === "textarea" ? rows : undefined}
            onChange={handleChange}
            error={hasError}
            helperText={errorMessage}
            variant={variant}
            sx={{
              fontFamily: "Inter",
              fontWeight: 400,
              color: "#181D27",
              width: "100%",
              borderColor: "#D5D7DA",
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "#D5D7DA",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#D5D7DA",
                  borderWidth: "1px",
                },
              },
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    {adornment as React.ReactNode}
                    {verified && (
                      <Box display="flex" alignItems="center" gap={1} mr={1}>
                        <CheckCircle fontSize="small" color="success" />
                        <Typography
                          sx={{
                            color: "#11A64A",
                            fontSize: "15px",
                            fontWeight: 500,
                          }}
                        >
                          Verified
                        </Typography>
                      </Box>
                    )}
                  </InputAdornment>
                ),
              },
              htmlInput: { maxLength: limit, ...inputProps },
            }}
          />
          {limit && (
            <Typography
              variant="body2"
              sx={{
                color: "#181D27",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "16px",
                textAlign: "left",
                marginTop: "4px",
              }}
            >
              {fieldValue?.length || 0}/{limit}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default DynamicInput;
